import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, fnViaAlias } from '../lib/apiClient';
import type { Device } from '../lib/types';
import { ModalShell } from './PresetEditorModal';

/**
 * Set-pump-clock modal — Web parity for the iOS `TimePickerModal`
 * surfaced from `DeviceDetailsView.swift` line 145 (sheet) / line 205
 * (button). Calls `POST /devices/set-clock/:deviceId` with
 * `{ timeHour, timeMinute }` (STRINGS — that matches the iOS request
 * shape; the handler in `setClock.js` normalises to integers
 * server-side).
 *
 * Two affordances:
 *   - Native `<input type="time">` picker, pre-filled with the pump's
 *     last reported clock (`device.timeSetting`).
 *   - "Use my current time" button that snaps to `new Date()` local
 *     wall-clock. Equivalent to the small clock icon in the top-right
 *     of the iOS modal.
 *
 * On Save: optimistic update of `device.timeSetting` while in flight;
 * the next poll/SSE state event will reconcile with the pump's actual
 * reported clock. Rolls back on error.
 *
 * Owner-only — followers can't reach the trigger button on PumpDetail.
 */
export function SetPumpClockModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Initial value: pump's reported clock, falling back to current local
  // wall-clock if the device hasn't reported one yet (new pumps emit
  // hours='0' minutes='0' until first telemetry).
  const [hhmm, setHhmm] = useState<string>(() => {
    const h = parseInt(device.timeSetting?.hours ?? '0', 10);
    const m = parseInt(device.timeSetting?.minutes ?? '0', 10);
    return formatHhmm(h, m);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Esc-to-close (matches PresetEditorModal pattern).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  function useNow() {
    const d = new Date();
    setHhmm(formatHhmm(d.getHours(), d.getMinutes()));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const parsed = parseHhmm(hhmm);
    if (!parsed) {
      setError('Please pick a valid time.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Body shape mirrors iOS — STRINGS, not numbers. Handler
      // (setClock.js:97-98) defaults to '0' when missing then Number()s
      // them, so strings work fine and stay wire-compatible with iOS.
      await fnViaAlias<null>(
        'POST',
        `/devices/set-clock/${encodeURIComponent(device.deviceId)}`,
        { timeHour: String(parsed.hour), timeMinute: String(parsed.minute) },
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unable to set the pump clock.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Set pump clock" onClose={submitting ? () => {} : onClose}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="mb-3">
          <label htmlFor="pump-clock-time" className="form-label small">
            New time
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="pump-clock-time"
              type="time"
              className="form-control"
              value={hhmm}
              onChange={(e) => setHhmm(e.target.value)}
              disabled={submitting}
              required
              style={{ maxWidth: 160 }}
            />
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={useNow}
              disabled={submitting}
              title="Set to my device's current time"
            >
              Use my current time
            </button>
          </div>
          <div className="form-text">
            Pump currently reports{' '}
            <strong style={{ color: 'var(--aqua-text-strong)' }}>
              {formatDisplay(device.timeSetting)}
            </strong>
            .
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 small mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Set clock'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/**
 * Format the pump's `timeSetting` for the read-only display row on the
 * Pump Info card. Matches the iOS DiagnosticRow value used at
 * `DeviceDetailsView.swift:238` (`device.timeSetting?.display`).
 *
 * Renders as `h:mm am/pm` (lowercase suffix, no leading zero on the
 * hour) so the layout reads the same as iOS. Falls back to an em-dash
 * when the pump hasn't reported a clock yet.
 */
export function formatDisplay(ts: Device['timeSetting'] | undefined): string {
  if (!ts) return '—';
  const h = parseInt(ts.hours ?? '0', 10);
  const m = parseInt(ts.minutes ?? '0', 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '—';
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mm = String(m).padStart(2, '0');
  return `${h12}:${mm} ${suffix}`;
}

function formatHhmm(h: number, m: number): string {
  const hh = String(Math.max(0, Math.min(23, h))).padStart(2, '0');
  const mm = String(Math.max(0, Math.min(59, m))).padStart(2, '0');
  return `${hh}:${mm}`;
}

function parseHhmm(s: string): { hour: number; minute: number } | null {
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  return { hour, minute };
}
